import config from '../config';

/**
 * Format a phone number to (XXX) XXX-XXXX format
 * @param {string} phoneNumber Phone number to format
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';
  
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if the input is exactly 10 digits
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  
  // If the input is not 10 digits, just return what was passed in
  return phoneNumber;
}

/**
 * Make an API call to the backend
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} - Response data
 */
export async function fetchApi(endpoint, options = {}) {
  // For frontend requests, use the Next.js API routes which will handle database connections
  // This uses relative URL rather than absolute to work with any deployment
  const url = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
  
  console.log(`Making API request to: ${url}`);
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  const fetchOptions = {
    ...defaultOptions,
    ...options,
  };

  try {
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      console.error(`API Error: ${response.status} ${response.statusText} for ${url}`);
      
      // Try to get error details from response
      let errorDetails = '';
      try {
        const errorData = await response.text();
        errorDetails = errorData ? ` - ${errorData}` : '';
      } catch (e) {
        // Ignore error reading response body
      }
      
      throw new Error(`API Error: ${response.status} ${response.statusText}${errorDetails}`);
    }
    
    try {
      return await response.json();
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError);
      throw new Error('Invalid response format from API');
    }
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * Get contractors 
 * @param {Object} params - Query parameters
 * @returns {Promise<Array>} - List of contractors
 */
export async function getContractors(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const endpoint = `/contractors${queryString ? `?${queryString}` : ''}`;
  return fetchApi(endpoint);
}

/**
 * Generate a URL-friendly slug from a string
 * @param {string} string - The string to slugify
 * @returns {string} - The slugified string
 */
export function slugify(string) {
  if (!string) return '';
  
  return string
    .toString()
    .normalize('NFD')                   // Split accented characters into base char + accent
    .replace(/[\u0300-\u036f]/g, '')   // Remove accent characters
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')              // Replace spaces with -
    .replace(/[^\w-]+/g, '')           // Remove all non-word chars
    .replace(/--+/g, '-')              // Replace multiple - with single -
    .replace(/^-+/, '')                // Trim - from start of text
    .replace(/-+$/, '');               // Trim - from end of text
}

/**
 * Get a specific contractor by ID or slug
 * @param {string} idOrSlug - Contractor ID or slug
 * @returns {Promise<Object>} - Contractor data
 */
export async function getContractorById(idOrSlug) {
  if (!idOrSlug) {
    console.error('getContractorById called without an ID or slug');
    throw new Error('Contractor ID or slug is required');
  }
  
  console.log(`Fetching contractor with ID/slug: ${idOrSlug}`);
  
  try {
    const data = await fetchApi(`/contractors/${idOrSlug}`);
    console.log(`Successfully fetched contractor ${idOrSlug}:`, data ? 'Data received' : 'No data');
    
    // If no data is returned, provide a minimal fallback object to prevent blank page
    if (!data) {
      console.warn(`No data returned for contractor ${idOrSlug}, using fallback`);
      return {
        id: idOrSlug,
        company_name: "Contractor information unavailable",
        phone: "Contact customer service",
        email: "",
        website: "",
        address: "Address unavailable",
        city: "",
        state: "",
        zip: "",
        services: [],
        rating: 0,
        reviews_count: 0,
        reviews: []
      };
    }
    
    // Generate SEO-friendly slug if not already present
    if (!data.slug && data.name) {
      // Create a base slug from the company name
      let slug = slugify(data.name);
      
      // Add location info to the slug if available for uniqueness
      if (data.city && data.state) {
        slug += `-${slugify(data.city)}-${data.state.toLowerCase()}`;
      }
      
      data.slug = slug;
    }
    
    return data;
  } catch (error) {
    console.error(`Error fetching contractor ${idOrSlug}:`, error);
    throw error;
  }
}

/**
 * Submit flood alert subscription
 * @param {Object} data - Form data
 * @returns {Promise<Object>} - Response data
 */
export async function subscribeToFloodAlerts(data) {
  return fetchApi('/flood-alerts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Send message to chatbot
 * @param {Object} message - Message data
 * @returns {Promise<Object>} - Chatbot response
 */
export async function sendChatbotMessage(message) {
  return fetchApi('/chatbot', {
    method: 'POST',
    body: JSON.stringify(message),
  });
}

/**
 * Get emergency information
 * @returns {Promise<Object>} - Emergency information
 */
export async function getEmergencyInfo() {
  return fetchApi('/emergency');
}

/**
 * Submit quiz results
 * @param {Object} data - Quiz answers
 * @returns {Promise<Object>} - Quiz results
 */
export async function submitQuizResults(data) {
  return fetchApi('/quiz-results', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Add a simple in-memory cache for search results 
const searchCache = {
  results: new Map(),
  // Clear items older than 1 minute
  maxAge: 60 * 1000
};

// Periodically clear old cache entries
if (typeof window !== 'undefined') {
  setInterval(() => {
    const now = Date.now();
    for (const [key, value] of searchCache.results.entries()) {
      if (now - value.timestamp > searchCache.maxAge) {
        searchCache.results.delete(key);
      }
    }
  }, 60 * 1000); // Clean up every minute
}

/**
 * Search for contractors or information
 * @param {Object} params - Search parameters
 * @returns {Promise<Object>} - Search results
 */
export async function search(params = {}) {
  const queryString = new URLSearchParams(params).toString();
  const endpoint = `/search${queryString ? `?${queryString}` : ''}`;
  
  // Debug to see what page is using search
  if (typeof window !== 'undefined') {
    console.log(`Search API called from ${window.location.pathname} with params:`, params);
  }
  
  // Create cache key from endpoint but exclude cache-busting parameters
  const cacheableParams = { ...params };
  delete cacheableParams.cacheBust; // Remove cache busting timestamp
  const cacheQueryString = new URLSearchParams(cacheableParams).toString();
  const cacheKey = `/search${cacheQueryString ? `?${cacheQueryString}` : ''}`;
  
  // Check cache first (except for first page loads or when cacheBust is provided)
  const forceFresh = params.cacheBust || params.forceRefresh;
  const cachedResult = !forceFresh ? searchCache.results.get(cacheKey) : null;
  
  if (cachedResult) {
    console.log(`Using cached search results for: ${cacheKey}`);
    return cachedResult.data;
  }
  
  try {
    // Increase timeout to 20 seconds for search queries
    const result = await fetchWithTimeout(endpoint, { timeout: 20000 });
    
    // Only cache successful responses with data
    if (result && result.success === true && Array.isArray(result.contractors) && result.contractors.length > 0) {
      // Store in cache with timestamp
      searchCache.results.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
    }
    
    return result;
  } catch (error) {
    console.error(`Search request failed: ${error.message}`);
    
    // If we have a cached version and this was a timeout error, return the cached version
    if (cachedResult && (error.message.includes('timeout') || error.message.includes('network'))) {
      console.log(`Falling back to cached search results after error`);
      return cachedResult.data;
    }
    
    // On non-search pages, return an empty result instead of throwing error
    if (typeof window !== 'undefined' && !window.location.pathname.includes('/search')) {
      console.log('Returning empty result for non-search page');
      return {
        success: true,
        contractors: [],
        message: 'No results available on non-search page'
      };
    }
    
    throw error;
  }
}

/**
 * Fetch with timeout
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Options including timeout in ms
 * @returns {Promise<any>} - Response data
 */
export async function fetchWithTimeout(endpoint, options = {}) {
  const { timeout = 15000, ...fetchOptions } = options;
  
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  
  try {
    const result = await fetchApi(endpoint, {
      ...fetchOptions,
      signal: controller.signal
    });
    clearTimeout(id);
    return result;
  } catch (error) {
    clearTimeout(id);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
}

/**
 * Send a POST request to the API
 * @param {string} endpoint - API endpoint path
 * @param {Object|FormData} data - Data to send
 * @param {boolean} isFormData - Whether data is FormData (for file uploads)
 * @returns {Promise<Object>} - Response data
 */
export async function postData(endpoint, data, isFormData = false) {
  // For frontend requests, use the Next.js API routes which will handle database connections
  const url = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
  
  console.log(`Making POST request to: ${url}`);
  
  let fetchOptions = {
    method: 'POST',
  };
  
  if (isFormData) {
    // For FormData (multipart/form-data), don't set Content-Type
    // The browser will set it with the correct boundary
    fetchOptions.body = data;
  } else {
    fetchOptions.headers = {
      'Content-Type': 'application/json',
    };
    fetchOptions.body = JSON.stringify(data);
  }
  
  try {
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      console.error(`API Error: ${response.status} ${response.statusText} for ${url}`);
      
      // Try to get error details from response
      let errorDetails = '';
      try {
        const errorData = await response.text();
        errorDetails = errorData ? ` - ${errorData}` : '';
      } catch (e) {
        // Ignore error reading response body
      }
      
      throw new Error(`API Error: ${response.status} ${response.statusText}${errorDetails}`);
    }
    
    try {
      return await response.json();
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError);
      throw new Error('Invalid response format from API');
    }
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}